/**
 * The vocabulary every card list sorts and filters by.
 *
 * One module, shared by the sheet that draws the controls, the client that
 * builds the catalogue query, and the in-memory sort the All tab and binders
 * use, so the three cannot drift. Every id here is also what goes into saved
 * preferences, so ids are stable words rather than indexes.
 *
 * The shape the owner settled on:
 *
 *   SORT is king, and single choice. It decides the order AND the separators,
 *   and inside a group cards fall into natural card-number order, which is
 *   what a collector expects when nothing else is asked for.
 *
 *   FILTER is multi-select and reorders nothing. It only includes and excludes.
 *   Grass and Lightning. Trainers and Energy. Commons and Uncommons. That split
 *   is what keeps the combinations harmless: any number of ticks still produces
 *   one well-defined order.
 *
 * What is deliberately absent. The catalogue row is a CARD, not a printing:
 * holo, reverse holo, first edition and stamps live in
 * `digitalcards.card_variants`, one row per printing, so a "holo only" control
 * here would filter the wrong table and quietly answer a different question.
 * Illustrator, regulation mark, weakness, retreat cost and stage are all real
 * fields and all rejected for one reason: they turn a screen meant to be
 * played with into a form.
 */

// ---------------------------------------------------------------- sorting ---

/**
 * The orders, in the order the Sort pane lists them.
 *
 * `groups` names the separator this order implies, or null where a separator
 * would be noise. `needsPrice` marks the two that cannot be answered without
 * the market value on the row.
 */
/**
 * `pair` puts an order and its opposite on one row of the sheet.
 *
 * The owner: "High to low on the left, low to high on the right. New set on the
 * left, old set on the right. So they're next to each other, and it's not one
 * big fat static list." It halves the height and it makes the relationship
 * visible, which a single column hides - Newest Set and Oldest Set are one
 * decision with a direction, not two unrelated options.
 *
 * Orders with no opposite carry no pair and span the full width. Sitting them
 * alone in the left column would leave a hole that reads as a missing option.
 * The order within a pair is always the "more" one first: highest, newest,
 * rarest, A first.
 */
export const CATALOG_SORTS = [
  { id: 'value-desc', label: 'Value High-Low', pair: 'value', groups: null, needsPrice: true },
  { id: 'value-asc', label: 'Value Low-High', pair: 'value', groups: null, needsPrice: true },
  { id: 'set-newest', label: 'Newest Set', pair: 'set', groups: 'set' },
  { id: 'set-oldest', label: 'Oldest Set', pair: 'set', groups: 'set' },
  { id: 'era-newest', label: 'Newest Era', pair: 'era', groups: 'era' },
  { id: 'era-oldest', label: 'Oldest Era', pair: 'era', groups: 'era' },
  { id: 'rarity-desc', label: 'Rarity High-Low', pair: 'rarity', groups: 'rarity' },
  { id: 'rarity-asc', label: 'Rarity Low-High', pair: 'rarity', groups: 'rarity' },
  { id: 'name-asc', label: 'Name A-Z', pair: 'name', groups: 'letter' },
  { id: 'name-desc', label: 'Name Z-A', pair: 'name', groups: 'letter' },
  { id: 'hp-desc', label: 'HP High-Low', pair: 'hp', groups: null },
  { id: 'hp-asc', label: 'HP Low-High', pair: 'hp', groups: null },
  { id: 'type', label: 'Type', groups: 'type' },
];

/**
 * The orders a SEARCH result can be put in.
 *
 * A search is a different shape from one set's cards: "magikarp" answers with
 * cards from thirty sets and twenty-five years, so the orders that matter are
 * the cross-set ones. The owner's list, verbatim: "my sort price high to low to
 * grab rare ones, or they filter only SIR, or they sort by old to new to get
 * the vintage".
 *
 * It is the catalogue's own list, not a second vocabulary, with two changes.
 *
 *   Best Match is added, and it is the default. It is what the search already
 *   did before it had a sort control - the prefix pass ahead of the contains
 *   pass, so typing "char" leads with Charizard rather than with Big Charm -
 *   and losing that to an alphabetical default would have been a regression
 *   dressed up as a feature. Sort is king, so relevance has to BE one of the
 *   orders rather than something layered underneath whichever order is chosen.
 *
 *   HP is dropped. It is the one catalogue order that says nothing across a
 *   search: every Magikarp has roughly the same HP, so the order is noise, and
 *   a sheet with two dead options in it teaches people to stop reading it.
 */
export const SEARCH_RELEVANCE_SORT = 'relevance';

export const SEARCH_SORTS = [
  { id: SEARCH_RELEVANCE_SORT, label: 'Best Match', groups: null },
  ...CATALOG_SORTS.filter((o) => o.pair !== 'hp'),
];

export const DEFAULT_SEARCH_SORT = SEARCH_RELEVANCE_SORT;

/**
 * Lay a flat list of sort options out in rows: a pair to a row, a lone option
 * across the whole width.
 * @returns {Array<Array<object>>}
 */
export function sortRows(options) {
  const rows = [];
  const seen = new Set();
  for (const option of options) {
    if (seen.has(option.id)) continue;
    if (!option.pair) { rows.push([option]); seen.add(option.id); continue; }
    const mate = options.find((o) => o.pair === option.pair && o.id !== option.id && !seen.has(o.id));
    seen.add(option.id);
    if (mate) { seen.add(mate.id); rows.push([option, mate]); } else rows.push([option]);
  }
  return rows;
}

export const DEFAULT_CATALOG_SORT = 'name-asc';

/** What the sheet says is happening while a sort runs. Present tense, a verb. */
export const SORT_VERB = {
  relevance: 'Searching',
  'value-desc': 'Ranking', 'value-asc': 'Ranking',
  'rarity-desc': 'Ranking', 'rarity-asc': 'Ranking',
  'name-asc': 'Sorting', 'name-desc': 'Sorting',
  'set-newest': 'Ordering', 'set-oldest': 'Ordering',
  'era-newest': 'Ordering', 'era-oldest': 'Ordering',
  'type': 'Grouping',
  'hp-desc': 'Ordering', 'hp-asc': 'Ordering',
};

/** The tail of the loading line: what the order actually is. */
export const SORT_PHRASE = {
  // Deliberately empty: "Searching 305 Magikarp cards" is the whole sentence,
  // and no tail a person would recognise describes relevance order.
  relevance: '',
  'value-desc': 'by value, highest first',
  'value-asc': 'by value, lowest first',
  'name-asc': 'A to Z',
  'name-desc': 'Z to A',
  'set-newest': 'by set, newest first',
  'set-oldest': 'by set, oldest first',
  'era-newest': 'by era, newest first',
  'era-oldest': 'by era, oldest first',
  'rarity-desc': 'by rarity, rarest first',
  'rarity-asc': 'by rarity, commonest first',
  'type': 'by type',
  'hp-desc': 'by HP, highest first',
  'hp-asc': 'by HP, lowest first',
};

/**
 * Which separator an order implies.
 *
 * The test is whether the label says something the cards do not.
 *
 *   set, era, rarity, type   yes. Each names the thing the order is built on,
 *                            and none of them is legible on a thumbnail: a set
 *                            is a tiny symbol, an era is nowhere on the card at
 *                            all, and a rarity is a single character in the
 *                            corner.
 *   name                     yes. A-Z by first letter is how every index a
 *                            person has ever used is broken up.
 *   value                    no. There is no natural boundary in a price run.
 *                            Any bucket would be invented, and the price is
 *                            already printed on every thumbnail.
 *   HP                       no, and more bluntly. HP is set in large type on
 *                            the card face. A "280 HP" heading over cards that
 *                            each say 280 HP is noise.
 */
export function catalogGrouping(sort) {
  // Both lists, because the search shares the catalogue's ids and adds one of
  // its own. A separator is a property of the ORDER, not of the screen, so
  // "Newest Set" has to draw set headings wherever it is chosen.
  const found = CATALOG_SORTS.find((s) => s.id === sort)
    || SEARCH_SORTS.find((s) => s.id === sort);
  return found ? found.groups : null;
}

// ---------------------------------------------------------------- filters ---

/**
 * The game's own colour for each energy type, in type-chart order rather than
 * alphabetical. A child who cannot yet read "Lightning" knows the yellow one,
 * and a row of eleven grey chips is a form where a row of eleven coloured ones
 * is a toy.
 */
export const CATALOG_TYPES = [
  'Grass', 'Fire', 'Water', 'Lightning', 'Psychic',
  'Fighting', 'Darkness', 'Metal', 'Dragon', 'Fairy', 'Colorless',
];

export const TYPE_COLORS = {
  Grass: '#4caf50',
  Fire: '#ff7043',
  Water: '#4fa8e8',
  Lightning: '#f5c518',
  Psychic: '#a95fd6',
  Fighting: '#d4622a',
  Darkness: '#5b6b7c',
  Metal: '#9aa5b1',
  Dragon: '#c9a227',
  Fairy: '#f06fae',
  Colorless: '#c2c8ce',
};

/** Pokemon, Trainer or Energy. The catalogue calls this `category`. */
export const CATALOG_KINDS = [
  { id: 'Pokemon', label: 'Pokemon' },
  { id: 'Trainer', label: 'Trainer' },
  { id: 'Energy', label: 'Energy' },
];

/**
 * The extras. Empty means everything; ticking one narrows to it.
 *
 * "Free" is TCG Pocket. Those cards were never printed and never sold, and the
 * owner's word for them is free rather than unpriced, so free is how you ask.
 */
export const CATALOG_SHOW = [
  { id: 'promos', label: 'Promos' },
  { id: 'free', label: 'Free' },
];

/**
 * Rarity, bucketed, low to high.
 *
 * The catalogue holds thirty distinct rarity strings, which is a spreadsheet
 * rather than a filter. These thirteen are the rungs a collector names out
 * loud, and each gathers every printed string that means it. Three decisions
 * worth stating:
 *
 *   "Holo Rare" and "Rare Holo" are the same rung spelled two ways, 605 cards
 *   split across two spellings by whichever vendor wrote the row. They fold
 *   together here, which is a bug fixed rather than a bucket invented.
 *
 *   Illustration Rare and Special Illustration Rare get their own choices
 *   rather than sharing one. They are what a child is actually hunting, and
 *   SIR is the abbreviation people say out loud.
 *
 *   The 821 cards with no rarity recorded become "Unlisted" instead of being
 *   quietly unreachable. They are mostly basic energy and old promos, and a
 *   filter that cannot reach 821 cards is a filter that lies.
 *
 * TCG Pocket runs its own ladder - diamonds, stars, shinies and a crown - and
 * it is folded into the same buckets rather than given a section of its own,
 * so a rarity filter can never be the thing that drops Pocket cards.
 *
 * `rank` matches the `rarity_rank` column the database sorts on, so the list
 * order here and the sort order there cannot disagree.
 */
export const RARITY_BUCKETS = [
  { id: 'common', label: 'Common', rank: 10, values: ['Common', 'One Diamond'] },
  { id: 'uncommon', label: 'Uncommon', rank: 20, values: ['Uncommon', 'Two Diamond'] },
  { id: 'rare', label: 'Rare', rank: 30, values: ['Rare', 'Three Diamond'] },
  { id: 'holo', label: 'Holo Rare', rank: 40, values: ['Holo Rare', 'Rare Holo', 'Classic Collection'] },
  { id: 'double', label: 'Double Rare', rank: 50, values: ['Double rare', 'Four Diamond'] },
  {
    id: 'ultra',
    label: 'Ultra Rare',
    rank: 60,
    values: [
      'Ultra Rare', 'Holo Rare V', 'Holo Rare VMAX', 'Holo Rare VSTAR',
      'Rare Holo LV.X', 'Rare PRIME', 'LEGEND', 'Radiant Rare', 'Amazing Rare',
      'ACE SPEC Rare', 'Full Art Trainer',
    ],
  },
  {
    id: 'illustration',
    label: 'Illustration Rare',
    rank: 70,
    values: ['Illustration rare', 'One Star', 'Two Star'],
  },
  // The 30th anniversary set's Pikachu run: 11 cards of one Pikachu each,
  // between the illustration rares in both price and feel, and its own choice
  // because a set of eleven Pikachus is a thing somebody hunts deliberately.
  { id: 'pikachu', label: 'Pikachu Rare', rank: 72, values: ['Pikachu Rare'] },
  {
    id: 'special-illustration',
    label: 'Special Illustration Rare',
    rank: 75,
    values: ['Special illustration rare', 'Three Star'],
  },
  {
    id: 'shiny',
    label: 'Shiny',
    rank: 80,
    values: ['Shiny rare', 'Shiny rare V', 'Shiny rare VMAX', 'Shiny Ultra Rare', 'One Shiny', 'Two Shiny'],
  },
  { id: 'hyper', label: 'Hyper Rare', rank: 85, values: ['Hyper rare', 'Mega Hyper Rare', 'Black White Rare'] },
  { id: 'secret', label: 'Secret Rare', rank: 88, values: ['Secret Rare', 'Crown'] },
  // Above Secret Rare, which is where the two cards carrying it actually sit:
  // Mew ex at 1,977 and Mewtwo ex at 875 are the most valuable cards in the set
  // that introduced the rarity.
  { id: 'futuristic', label: 'Futuristic Rare', rank: 90, values: ['Futuristic Rare'] },
  // Promo is how a card was handed out, not how rare it is - a giveaway promo
  // is not rarer than a Secret Rare - so it is off the ladder too. Membership
  // is decided by the SET in the database, not by this list: 995 cards, of
  // which only 361 carry the literal rarity 'Promo'. The rest are promo-line
  // sets, McDonald's Collection, Best of Game and Futsal, none of which have a
  // rarity symbol printed on them at all.
  { id: 'promo', label: 'Promo', rank: null, values: ['Promo'] },
  // Was "Unlisted", which was an honest placeholder for "we do not know" and is
  // no longer true: these 249 are from starter decks and trainer kits, and deck
  // product cards do not get a rarity symbol printed on them. The data was
  // never missing. "Decks & Kits" is concrete, and it is something a child
  // could deliberately go looking for, which Unlisted never was.
  //
  // Membership is by set, in the database: every tk-* trainer kit, plus Kalos
  // Starter Set, My First Battle, Pokemon Rumble and Poke Card Creator Pack.
  { id: 'decks', label: 'Decks & Kits', rank: null, values: [] },
];

/** The rung a raw rarity string sits on, matching the database's rarity_rank. */
const RANK_BY_RARITY = (() => {
  const map = new Map();
  for (const b of RARITY_BUCKETS) {
    if (b.rank == null) continue;
    // Keyed lower case, because the two catalogues disagree on capitalisation
    // for the same rung: TCGdex writes "Double rare", the presale feed writes
    // "Double Rare". An exact match put all 22 of a set's cards in the promo
    // bucket. The database's own CASE compares on lower() for the same reason.
    for (const v of b.values) map.set(v.toLowerCase(), b.rank);
  }
  return map;
})();

/**
 * The rung a raw rarity string sits on, or null for the ones that are not on
 * the ladder. In-memory sorts read this too, so the All tab and the
 * catalogue agree; they push null to the end explicitly.
 */
export function rarityRankOf(rarity) {
  return RANK_BY_RARITY.get(String(rarity ?? '').toLowerCase()) ?? null;
}

/** The bucket a raw rarity string belongs to. */
export function rarityBucketOf(rarity) {
  const key = String(rarity ?? '').toLowerCase();
  return RARITY_BUCKETS.find((b) => b.values.some((v) => v.toLowerCase() === key))
    || RARITY_BUCKETS.find((b) => b.id === 'promo');
}

/**
 * Every raw rarity string behind a list of bucket ids.
 *
 * Only used by the in-memory sorts now. The catalogue filter matches the
 * `rarity_bucket` column instead, because the two bottom buckets are defined by
 * the SET a card came from and the rarity string is precisely the thing that is
 * absent from them.
 */
export function rarityValues(bucketIds) {
  const ids = new Set(bucketIds || []);
  const out = [];
  for (const b of RARITY_BUCKETS) if (ids.has(b.id)) out.push(...b.values);
  return out;
}

/**
 * The sets whose cards carry no rarity symbol because they came out of a deck
 * or a kit rather than a pack. Mirrors the `rarity_bucket` CASE in the
 * materialized view exactly; both must move together.
 *
 * Every `tk-*` trainer kit, plus the four product sets that do not follow that
 * naming: Kalos Starter Set, My First Battle, Pokemon Rumble, Poke Card
 * Creator Pack.
 */
const DECK_KIT_SET_IDS = new Set(['xy0', 'mfb', 'ru1', 'ex5.5']);

export function isDeckOrKitSet(setId) {
  const id = String(setId || '');
  return id.startsWith('tk-') || DECK_KIT_SET_IDS.has(id);
}

/**
 * The bucket for a card held in someone's collection.
 *
 * The catalogue's own rows carry `rarity_bucket`, worked out once in the
 * database. A collection row does not: it stores whatever rarity string was
 * captured at scan time, and for the two bottom buckets that string is exactly
 * what is missing. So the same set-based rule is applied here, which is why it
 * lives in this module rather than being written twice.
 */
export function bucketForCard(rarity, setId) {
  const known = RARITY_BUCKETS.find((b) => b.values.includes(rarity));
  if (known) return known;
  return RARITY_BUCKETS.find((b) => b.id === (isDeckOrKitSet(setId) ? 'decks' : 'promo'));
}

/** The set id inside a catalogue card id: `${setId}-${localId}`, last dash. */
export function setIdFromCatalogId(cardId) {
  if (!cardId) return null;
  const raw = String(cardId);
  const cut = raw.lastIndexOf('-');
  return cut > 0 ? raw.slice(0, cut) : null;
}

/** Nothing ticked, for a collection screen. Same shape, fewer groups. */
export const EMPTY_COLLECTION_FILTERS = {
  types: [],
  rarities: [],
  eras: [],
};

/** A saved bucket id from before the rename. */
function migrateBucketId(id) {
  return id === 'unlisted' ? 'decks' : id;
}

// ------------------------------------------------------------ filter state --

/** Nothing ticked. Every group is a list, and empty means "do not narrow". */
export const EMPTY_CATALOG_FILTERS = {
  show: [],
  kinds: [],
  types: [],
  rarities: [],
  eras: [],
};

const GROUPS = ['show', 'kinds', 'types', 'rarities', 'eras'];

/** Read a group as an array, whatever shape an older saved preference is in. */
export function filterList(filters, group) {
  const v = filters ? filters[group] : null;
  if (Array.isArray(v)) return v;
  // Preferences saved before filters went multi-select held single strings.
  // Reading them as a one-item list means nobody loses their saved choice to
  // a release.
  if (typeof v === 'string' && v && v !== 'all') return [v];
  return [];
}

/** A saved preference of any vintage, normalised to the multi-select shape. */
export function normaliseFilters(filters) {
  const out = { ...EMPTY_CATALOG_FILTERS };
  if (!filters) return out;
  out.show = filterList(filters, 'show');
  out.kinds = filterList(filters, 'kinds').length
    ? filterList(filters, 'kinds') : filterList(filters, 'kind');
  out.types = filterList(filters, 'types').length
    ? filterList(filters, 'types') : filterList(filters, 'type');
  out.rarities = (filterList(filters, 'rarities').length
    ? filterList(filters, 'rarities') : filterList(filters, 'rarity')).map(migrateBucketId);
  out.eras = filterList(filters, 'eras').length
    ? filterList(filters, 'eras') : filterList(filters, 'era');
  return out;
}

/** Tick or untick one value in one group, without mutating the original. */
export function toggleFilter(filters, group, value) {
  const current = filterList(filters, group);
  const next = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  return { ...normaliseFilters(filters), [group]: next };
}

/** Is anything actually narrowing the list? */
export function hasCatalogFilters(filters) {
  return GROUPS.some((g) => filterList(filters, g).length > 0);
}

/** How many groups are on, for the badge on the toolbar button. */
export function countCatalogFilters(filters) {
  return GROUPS.reduce((n, g) => n + (filterList(filters, g).length ? 1 : 0), 0);
}

/** How many individual choices are ticked, for the Filter tab's own count. */
export function countCatalogChoices(filters) {
  return GROUPS.reduce((n, g) => n + filterList(filters, g).length, 0);
}

/**
 * The filters in words, for the note bar and the loading line.
 *
 * Multi-select means this has to read as a sentence rather than a list of
 * tokens: "Grass and Fire Pokemon", "Secret Rare and Hyper Rare cards". Past
 * three things in one group it counts instead, because "Grass, Fire, Water,
 * Lightning and Psychic cards" is longer than the bar it sits in.
 */
function joinWords(list, noun) {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  if (list.length === 3) return `${list[0]}, ${list[1]} and ${list[2]}`;
  return `${list.length} ${noun}`;
}

export function describeCatalogFilters(filters, eras = null) {
  if (!hasCatalogFilters(filters)) return 'cards';
  // Groups are separated by commas and joined inside themselves by "and", so
  // two types and two rarities read as "Illustration Rare and Special
  // Illustration Rare, Grass and Lightning cards" rather than as one run of
  // words with no seam in it.
  const groups = [];

  const show = filterList(filters, 'show');
  const showWords = [];
  if (show.includes('promos')) showWords.push('promo');
  if (show.includes('free')) showWords.push('free');
  if (showWords.length) groups.push(showWords.join(' and '));

  const rarities = filterList(filters, 'rarities')
    .map((id) => RARITY_BUCKETS.find((b) => b.id === id)?.label)
    .filter(Boolean);
  if (rarities.length) groups.push(joinWords(rarities, 'rarities'));

  const types = filterList(filters, 'types');
  if (types.length) groups.push(joinWords(types, 'types'));

  const kinds = filterList(filters, 'kinds');
  if (kinds.length) groups.push(joinWords(kinds, 'kinds'));

  let text = `${groups.join(', ')} cards`.trim();

  const eraIds = filterList(filters, 'eras');
  if (eraIds.length) {
    const names = eraIds
      .map((id) => shortEraLabel((eras || []).find((e) => e.id === id) || { id }))
      .filter(Boolean);
    text += ` from ${joinWords(names, 'eras')}`;
  }
  // Only the first letter. The names inside already carry their own casing,
  // and a CSS capitalize over the whole line turned every "and" into "And".
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// --------------------------------------------------------------- grouping ---

/**
 * An era's name, short enough to be a chip.
 *
 * The catalogue's own display names are sentences - "English Wizards of the
 * Coast Era (1999-2003)" - which is right above a grid of sets and wrong on a
 * chip, where thirteen of them wrapped to five rows and pushed the sort list,
 * the actual point of the sheet, off the bottom of the screen. The language,
 * the word "Era" and the year range are obvious from context or repeated on
 * every single chip.
 */
export function shortEraLabel(era) {
  const raw = String(era?.display_name || era?.id || '');
  return raw
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/^English\s+/i, '')
    .replace(/^Pokemon\s+/i, '')
    .replace(/\s+Era$/i, '')
    .trim() || String(era?.id || '');
}

/**
 * The group a card belongs to under a grouping mode.
 *
 * Era order returns a DUAL label - "Scarlet & Violet · Pitch Black" - and
 * groups on the set rather than on the era. That is the owner's "dual label
 * era-set", and grouping on the finer of the two is what makes it work: era
 * order runs era, then set, then card number, so the set changes many times
 * inside one era. A header per era would leave two hundred cards under one
 * heading, and a second level of heading inside a flat grid is clutter. One
 * line naming both keeps every header informative and never repeats.
 *
 * @returns {{ key: string, label: string, meta: string }}
 */
export function catalogGroupOf(mode, card, eras = null) {
  if (mode === 'set') {
    return {
      key: card.set_id || card.set_name || 'other',
      label: card.set_name || card.set_id || 'Other',
      meta: card.release_date ? String(card.release_date).slice(0, 4) : '',
    };
  }
  if (mode === 'era') {
    const era = (eras || []).find((e) => e.id === card.era_id);
    const eraName = shortEraLabel(era || { display_name: card.era_name, id: card.era_id });
    const setName = card.set_name || card.set_id || 'Other';
    return {
      key: card.set_id || setName,
      label: eraName ? `${eraName} · ${setName}` : setName,
      meta: card.release_date ? String(card.release_date).slice(0, 4) : '',
    };
  }
  if (mode === 'rarity') {
    // The row carries its bucket, worked out in the database from the set where
    // the rarity string could not say. Falling back to the string only matters
    // for rows that predate the column.
    const bucket = RARITY_BUCKETS.find((b) => b.id === card.rarity_bucket)
      || rarityBucketOf(card.rarity);
    return { key: bucket.id, label: bucket.label, meta: '' };
  }
  if (mode === 'type') {
    const t = card.primary_type || card.types?.[0] || null;
    return { key: t || 'none', label: t || 'No type', meta: '' };
  }
  // Anything not starting with a letter - "&", a number - files under "#", the
  // way a phone book does, rather than a heading per punctuation mark.
  const first = String(card.name || '').trim().charAt(0).toUpperCase();
  const key = /[A-Z]/.test(first) ? first : '#';
  return { key, label: key, meta: '' };
}
